import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import { useAuth } from "../../auth/AuthContext";
import { useDeployment } from "../../deployment/DeploymentContext";
import { InstallationId, LicenseUpload } from "../license/LicenseUpload";

/** First-run setup of an on-premise install: activate the license, then name the organisation and
 *  create its first administrator. Shown only while no organisation exists yet. */
export default function SetupWizard() {
  const navigate = useNavigate();
  const { startSession } = useAuth();
  const { status, refresh } = useDeployment();

  const licenseReady = status?.status === "ACTIVE" || status?.status === "EXPIRING";
  const [step, setStep] = useState<1 | 2>(licenseReady ? 2 : 1);
  const [licenseKey, setLicenseKey] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function continueFromLicense(text: string) {
    if (!text.trim()) return;
    setLicenseKey(text);
    setError(null);
    setStep(2);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError("Choose a password of at least 8 characters.");
    if (password !== confirm) return setError("The two passwords don't match.");
    setSubmitting(true);
    try {
      const res = await adminApi.setup.initialize({
        licenseKey: licenseReady ? undefined : licenseKey,
        organisationName: organisationName.trim(),
        adminName: adminName.trim() || undefined,
        adminEmail: adminEmail.trim(),
        adminPassword: password,
      });
      startSession(res, adminEmail.trim());
      await refresh();
      navigate("/getting-started", { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Setup failed";
      setError(message);
      // A license problem can only be fixed on step 1.
      if (/licen/i.test(message) && !licenseReady) setStep(1);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="setup-shell">
      <div className="setup-card">
        <div className="setup-brand">
          <div className="app-brand-mark">RF</div>
          <div>
            <div className="setup-title">Set up ReportForge</div>
            <div className="setup-sub">On-premise installation</div>
          </div>
        </div>

        <ol className="stepper">
          <li className={step === 1 ? "step step-active" : "step step-done"}>
            <span>{step === 1 ? "1" : "✓"}</span> License
          </li>
          <li className={step === 2 ? "step step-active" : "step"}>
            <span>2</span> Organisation &amp; administrator
          </li>
        </ol>

        {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}

        {step === 1 && (
          <div>
            <h3>Activate your license</h3>
            <p className="help-text">
              Upload the license file you received from the vendor. To get one, send them this
              installation's ID — the license can be issued for this server specifically.
            </p>
            <div className="form-row" style={{ maxWidth: "none" }}>
              <label>Installation ID</label>
              <InstallationId id={status?.installationId ?? null} />
            </div>
            <LicenseUpload
              submitLabel="Continue"
              initialText={licenseKey}
              onTextChange={setLicenseKey}
              onSubmit={continueFromLicense}
            />
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <h3>Your organisation</h3>
            {licenseReady ? (
              <p className="help-text">
                License active{status?.licensee ? <> for <strong>{status.licensee}</strong></> : null}. Now create the
                organisation and its first administrator.
              </p>
            ) : (
              <p className="help-text">
                License loaded. It's verified when you finish — if there's a problem you'll be brought back to fix it.{" "}
                <button type="button" className="link-button" onClick={() => setStep(1)}>Change license</button>
              </p>
            )}
            <div className="form-row" style={{ maxWidth: "none" }}>
              <label htmlFor="org">Organisation name</label>
              <input id="org" required value={organisationName} onChange={(e) => setOrganisationName(e.target.value)} placeholder="Mukumu SACCO Society Ltd" />
            </div>
            <div className="form-grid">
              <div className="form-row">
                <label htmlFor="aname">Administrator name</label>
                <input id="aname" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Jane Wanjiku" />
              </div>
              <div className="form-row">
                <label htmlFor="aemail">Administrator email</label>
                <input id="aemail" type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@yourorg.com" />
              </div>
              <div className="form-row">
                <label htmlFor="apass">Password</label>
                <input id="apass" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="form-row">
                <label htmlFor="aconfirm">Confirm password</label>
                <input id="aconfirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
            </div>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Setting up..." : "Finish setup"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
